import {
  type FetchSuggestionsProvider,
  type ListSuggestionsProvider,
  type SuggestionPage,
  type SuggestionsProvider,
  listSuggestions,
  suggestionPage,
  withLeading,
  withoutCounts,
} from "@/shared/components/filters/suggestions";
import {
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
  filterToCondition,
} from "@/shared/components/ResourceView";
import type {
  AggregateMetricsRequest,
  NumericAggregateMetricsResponse,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";
import type { Condition } from "@/types";

import { isGroupable } from "../dashboardFilters/groupableFields";

import { andConditions, type BucketWindow } from "./bucketWindow";
import { bucketSeriesCounts } from "./seriesSuggestions";

/**
 * The aggregate request that counts a clicked bucket's sessions by each value
 * of `field`: the chart's own request (see `aggregateRequestFor`), narrowed to
 * the bucket and to the drill-down's filters, and split by `field`.
 *
 * Only an aggregated card's bucket can be counted this way. The endpoint snaps
 * its window outward to whole periods, which leaves a period bucket as it is
 * but would widen a raw card's instant to the whole period around it.
 */
export function bucketCountsRequest(args: {
  metricName: string;
  period: Period;
  aggregation: NumericAggregation;
  bucket: BucketWindow;
  deviceIds?: string[];
  sessionIds?: string[];
  /** The dashboard's filter condition: what the chart was drawn under. */
  condition?: Condition;
  /** The drill-down's own filters that apply to this field's counts (see
   *  {@link facetCondition}). */
  drillCondition?: Condition;
  field: string;
}): AggregateMetricsRequest {
  return {
    name: args.metricName,
    period: args.period,
    aggregation: args.aggregation,
    time_filter: "end_time",
    start_time: args.bucket.start_time,
    end_time: args.bucket.end_time,
    include_device_ids: args.deviceIds,
    include_session_ids: args.sessionIds,
    condition: andConditions(args.condition, args.drillCondition),
    group_by: args.field,
  };
}

/**
 * The drill-down's filters that narrow `field`'s counts, so that a value's
 * count is the rows with that value the table would list once it's ticked.
 *
 * - Matching all, that's every filter but the field's own. The values of a
 *   field without a filter then add up to the table, and a filtered field's
 *   unticked values still say what ticking them would add.
 * - Matching any, a ticked value lists all its rows whatever the other filters
 *   say, so none narrow it.
 */
export function facetCondition(
  filters: readonly Filter[],
  field: string,
  options: { matchMode: FilterMatchMode; definitions?: FilterDefinition[] },
): Condition | undefined {
  if (options.matchMode === "OR") {
    return undefined;
  }
  return filterToCondition(
    filters.filter((filter) => filter.field !== field),
    options,
  );
}

/** A bucket's values of one field, counted. */
export interface BucketValues {
  /** The values with sessions in the bucket, most first, with their counts
   *  and the unset sessions'. */
  leading: ListSuggestionsProvider;
  /** Whether `leading` holds every value in the bucket. The server keeps only
   *  the most common values of a field with many; the unset count is never
   *  dropped. */
  complete: boolean;
}

/** The values `response` counts in `bucket`, most sessions first. */
export function bucketValues(
  response: NumericAggregateMetricsResponse,
  bucket: BucketWindow,
): BucketValues {
  const counts =
    bucketSeriesCounts(response.records, bucket) ??
    new Map<string | null, number>();
  // A stable sort, so tied values keep the server's order.
  const named = [...counts]
    .filter((entry): entry is [string, number] => entry[0] !== null)
    .sort(([, a], [, b]) => b - a);
  return {
    leading: listSuggestions(
      named.map(([value]) => value),
      {
        hasUnset: counts.has(null),
        counts: Object.fromEntries(named),
        unsetCount: counts.get(null) ?? 0,
      },
    ),
    complete: response.groups_truncated !== true,
  };
}

/** Where one field's counts in a bucket come from. */
export interface BucketCountsSource {
  /** Names the bucket, the filters and the field, for caching. */
  key: string;
  load: (signal: AbortSignal) => Promise<BucketValues>;
}

/**
 * `provider`'s suggestions, led by the bucket's values and counted in the
 * bucket: how many of its sessions hold each value. A listed value the bucket
 * lacks counts 0, unless the server's cap may have dropped it, when it shows
 * no count. Nothing is fetched until the values are listed.
 *
 * Where the bucket can't be counted, `provider`'s values are listed without
 * counts: they count a different scope, and filtering doesn't wait on a count.
 */
export function bucketCountedSuggestions(
  source: BucketCountsSource,
  provider: SuggestionsProvider | undefined,
): FetchSuggestionsProvider {
  const uncounted = provider && withoutCounts(provider);
  return {
    kind: "fetch",
    key: `bucket-counts:${source.key}|${providerKey(provider)}`,
    minSearchLength:
      provider?.kind === "fetch" ? provider.minSearchLength : undefined,
    fetch: async (search, signal) => {
      let values: BucketValues;
      try {
        values = await source.load(signal);
      } catch (error) {
        if (signal.aborted || uncounted === undefined) {
          throw error;
        }
        return suggestionPage(uncounted, search, signal);
      }
      const page = await suggestionPage(
        withLeading(values.leading, uncounted),
        search,
        signal,
      );
      return values.complete ? withZeroCounts(page) : page;
    },
  };
}

/** `page` with every value it doesn't count counted as 0. */
function withZeroCounts(page: SuggestionPage): SuggestionPage {
  const counts = { ...page.counts };
  for (const value of page.values) {
    counts[value] ??= 0;
  }
  return { ...page, counts };
}

/** What `provider`'s pages are cached under. */
function providerKey(provider: SuggestionsProvider | undefined): string {
  if (provider === undefined) {
    return "none";
  }
  if (provider.kind === "fetch") {
    return provider.key;
  }
  return `list:${JSON.stringify([
    provider.values,
    provider.hasUnset ?? null,
    provider.counts ?? null,
    provider.unsetCount ?? null,
  ])}`;
}

/**
 * The drill-down's filter rows, each counting the rows the drill-down lists.
 *
 * - Where the bucket can be counted (`countsFor`, an aggregated card's), every
 *   string row the server can split it by counts its values in the bucket, the
 *   group-by row included (see {@link bucketCountedSuggestions}).
 * - On a raw card, the group-by row keeps the counts the clicked points gave
 *   it (see `withSeriesSuggestions`).
 * - Every other row loses its counts: they are the dashboard's, counted across
 *   the org and over all time, which read as the drill-down's own.
 */
export function withBucketCounts(
  definitions: FilterDefinition[] | undefined,
  {
    groupBy,
    countsFor,
  }: {
    groupBy?: string;
    /** The bucket's counts of a field, or `undefined` where the window can't
     *  be counted. */
    countsFor: ((field: string) => BucketCountsSource) | undefined;
  },
): FilterDefinition[] | undefined {
  return definitions?.map((definition) => {
    if (definition.type !== "string") {
      return definition;
    }
    if (countsFor !== undefined && isGroupable(definition)) {
      return {
        ...definition,
        suggestions: bucketCountedSuggestions(
          countsFor(definition.field),
          definition.suggestions,
        ),
      };
    }
    if (definition.field === groupBy || definition.suggestions === undefined) {
      return definition;
    }
    return {
      ...definition,
      suggestions: withoutCounts(definition.suggestions),
    };
  });
}
